import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';
import { API_TIMEOUT } from '@/config/constants';
import { sessionStorage as storage } from '@/lib/storage';
import { installMockAdapter } from '@/mocks/mock-adapter';

let httpClient: AxiosInstance | null = null;
let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: unknown) => void }[] = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token!);
  });
  failedQueue = [];
}

function createHttpClient(): AxiosInstance {
  const client = axios.create({
    baseURL: env.API_BASE_URL,
    timeout: API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Attach JWT token to every request
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = storage.getAccessToken();
      if (token) {
        config.headers.Authorization = token;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  // Handle response errors with token refresh on 401
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!axios.isAxiosError(error)) return Promise.reject(error);

      const originalRequest = error.config;
      const status = error.response?.status;

      // 401 — attempt token refresh
      if (status === 401 && originalRequest && !(originalRequest as { _retry?: boolean })._retry) {
        if (isRefreshing) {
          // Queue this request until refresh completes
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token: string) => {
                originalRequest.headers.Authorization = token;
                resolve(client(originalRequest));
              },
              reject,
            });
          });
        }

        (originalRequest as { _retry?: boolean })._retry = true;
        isRefreshing = true;

        try {
          const currentToken = storage.getAccessToken();
          if (!currentToken) throw new Error('No token to refresh');

          const response = await axios.post(
            `${env.AUTH_BASE_URL}/RefreshToken`,
            {},
            {
              headers: {
                Authorization: currentToken,
                'Content-Type': 'application/json',
              },
              timeout: API_TIMEOUT,
            },
          );

          const newToken = response.data?.accessToken;
          if (!newToken) throw new Error('No token in refresh response');

          storage.setAccessToken(newToken);
          processQueue(null, newToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = newToken;
          return client(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Refresh failed — clear auth and redirect to login
          storage.clearAuth();
          window.dispatchEvent(new CustomEvent('auth-cleared'));
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // 403 — forbidden
      if (status === 403) {
        return Promise.reject(error);
      }

      return Promise.reject(error);
    },
  );

  // Install mock adapter if enabled
  if (import.meta.env.VITE_USE_MOCKS === 'true') {
    installMockAdapter(client);
    console.log('[Mock API] Authenticated client mocks installed');
  }

  return client;
}

export function getHttpClient(): AxiosInstance {
  if (!httpClient) {
    httpClient = createHttpClient();
  }
  return httpClient;
}

/**
 * Normalises API responses across mock and real server.
 * Mock: wraps data in NAInterfaceResponseObj.containerObj
 * Real API: returns the payload object directly
 */
export function unwrapContainer<T>(data: unknown): T {
  const d = data as Record<string, unknown>;
  if (d !== null && typeof d === 'object' && 'containerObj' in d) {
    return d.containerObj as T;
  }
  return data as T;
}
